import { EditorThemeClasses, Klass, LexicalNode, LexicalNodeReplacement, TextNode } from "lexical";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import {
  ArrowUUpLeftIcon,
  ArrowUUpRightIcon,
  TextBIcon,
  TextItalicIcon,
  TextUnderlineIcon,
  TextStrikethroughIcon,
  PaletteIcon,
  PaintRollerIcon,
  ParagraphIcon,
  TextHOneIcon,
  TextHTwoIcon,
  TextHThreeIcon,
  ListBulletsIcon,
  ListNumbersIcon,
  ListChecksIcon,
  QuotesIcon,
  LinkIcon,
  CodeIcon,
  TableIcon,
  MinusIcon,
  TextIndentIcon,
  TextOutdentIcon,
  CodeSimpleIcon,
  FunctionIcon,
} from "@phosphor-icons/react";

import { EditorColor } from "@/types/common";
import { ICON_SIZE } from "@/config/constants";
import { ExtendedTextNode } from "./components/Editor/nodes/ExtendedTextNode";
import { EquationNode } from "./components/Editor/nodes/EquationNode";
import { ToolbarItem, ToolbarItemKey } from "./types";

export const LEXICAL_COMPOSE_CONFIG: {
  nodes: Array<Klass<LexicalNode> | LexicalNodeReplacement>;
  theme: EditorThemeClasses;
} = {
  nodes: [
    AutoLinkNode,
    CodeNode,
    CodeHighlightNode,
    EquationNode,
    ExtendedTextNode,
    {
      replace: TextNode,
      with: (node: TextNode) => new ExtendedTextNode(node.__text),
      withKlass: ExtendedTextNode,
    },
    HeadingNode,
    HorizontalRuleNode,
    LinkNode,
    ListNode,
    ListItemNode,
    TableCellNode,
    TableNode,
    TableRowNode,
    QuoteNode,
  ],
  theme: {
    link: "e_a",
    hr: "e_hr",
    hrSelected: "e_hrs",
    paragraph: "e_p",
    quote: "e_q",
    text: {
      bold: "e_b",
      code: "e_c",
      italic: "e_i",
      underline: "e_u",
      strikethrough: "e_s",
    },
    list: {
      ol: "e_ol",
      ul: "e_ul",
      checklist: "e_cl",
      listitem: "e_li",
      listitemChecked: "e_lic",
      listitemUnchecked: "e_liu",
      nested: {
        listitem: "e_nli",
      },
    },
    heading: {
      h1: "e_h1",
      h2: "e_h2",
      h3: "e_h3",
    },
    table: "e_t",
    tableCellSelected: "e_tcs",
    tableScrollableWrapper: "e_tsw",
    code: "cb",
    codeHighlight: {
      atrule: "cb_a",
      attr: "cb_a",
      boolean: "cb_p",
      builtin: "cb_s",
      cdata: "cb_c",
      char: "cb_s",
      class: "cb_f",
      "class-name": "cb_f",
      comment: "cb_c",
      constant: "cb_p",
      deleted: "cb_d",
      doctype: "cb_c",
      entity: "cb_o",
      function: "cb_f",
      important: "cb_v",
      inserted: "cb_i",
      keyword: "cb_a",
      namespace: "cb_v",
      number: "cb_p",
      operator: "cb_o",
      prolog: "cb_c",
      property: "cb_p",
      punctuation: "cb_pt",
      regex: "cb_v",
      selector: "cb_s",
      string: "cb_s",
      symbol: "cb_p",
      tag: "cb_p",
      unchanged: "cb_u",
      url: "cb_o",
      variable: "cb_v",
    },
  },
};

export const TEXT_COLORS: EditorColor[] = [
  { name: "Gray", value: "var(--gray-text)", className: "editor-color-gray-text" },
  { name: "Blue", value: "var(--blue-text)", className: "editor-color-blue-text" },
  { name: "Purple", value: "var(--purple-text)", className: "editor-color-purple-text" },
  { name: "Pink", value: "var(--pink-text)", className: "editor-color-pink-text" },
  { name: "Red", value: "var(--red-text)", className: "editor-color-red-text" },
  { name: "Orange", value: "var(--orange-text)", className: "editor-color-orange-text" },
  { name: "Yellow", value: "var(--yellow-text)", className: "editor-color-yellow-text" },
  { name: "Green", value: "var(--green-text)", className: "editor-color-green-text" },
  { name: "Default", value: null, className: "border bg-content1 border-default" },
];

export const TEXT_COLORS_VALUES = TEXT_COLORS.map((color) => color.value).filter((color) => color !== null);

export const TEXT_BG_COLORS: EditorColor[] = [
  { name: "Gray", value: "var(--gray-bg)", className: "editor-color-gray-bg" },
  { name: "Blue", value: "var(--blue-bg)", className: "editor-color-blue-bg" },
  { name: "Purple", value: "var(--purple-bg)", className: "editor-color-purple-bg" },
  { name: "Pink", value: "var(--pink-bg)", className: "editor-color-pink-bg" },
  { name: "Red", value: "var(--red-bg)", className: "editor-color-red-bg" },
  { name: "Orange", value: "var(--orange-bg)", className: "editor-color-orange-bg" },
  { name: "Yellow", value: "var(--yellow-bg)", className: "editor-color-yellow-bg" },
  { name: "Green", value: "var(--green-bg)", className: "editor-color-green-bg" },
  { name: "Default", value: null, className: "bg-content1 border border-default" },
];

export const TEXT_BG_COLORS_VALUES = TEXT_BG_COLORS.map((color) => color.value).filter((color) => color !== null);

export const EDITOR_CONTENT_EMPTY_VALUE =
  '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}';

export const TOOLBAR_ITEMS_MAP: Record<ToolbarItemKey, ToolbarItem> = {
  undo: {
    id: "undo",
    label: "Undo",
    description: "Undo the last action",
    icon: <ArrowUUpLeftIcon size={ICON_SIZE.sm} />,
  },
  redo: {
    id: "redo",
    label: "Redo",
    description: "Redo the last undone action",
    icon: <ArrowUUpRightIcon size={ICON_SIZE.sm} />,
  },
  bold: { id: "bold", label: "Bold", description: "Make text bold", icon: <TextBIcon size={ICON_SIZE.sm} /> },
  italic: {
    id: "italic",
    label: "Italic",
    description: "Make text italic",
    icon: <TextItalicIcon size={ICON_SIZE.sm} />,
  },
  underline: {
    id: "underline",
    label: "Underline",
    description: "Underline text",
    icon: <TextUnderlineIcon size={ICON_SIZE.sm} />,
  },
  strikethrough: {
    id: "strikethrough",
    label: "Strikethrough",
    description: "Strike through text",
    icon: <TextStrikethroughIcon size={ICON_SIZE.sm} />,
  },
  textColor: {
    id: "textColor",
    label: "Text Color",
    description: "Change text color",
    icon: <PaletteIcon size={ICON_SIZE.sm} />,
  },
  backgroundColor: {
    id: "backgroundColor",
    label: "Background Color",
    description: "Change background color",
    icon: <PaintRollerIcon size={ICON_SIZE.sm} />,
  },
  paragraph: {
    id: "paragraph",
    label: "Paragraph",
    description: "Format as paragraph",
    icon: <ParagraphIcon size={ICON_SIZE.sm} />,
  },
  heading1: {
    id: "heading1",
    label: "Heading 1",
    description: "Format as heading 1",
    icon: <TextHOneIcon size={ICON_SIZE.sm} />,
  },
  heading2: {
    id: "heading2",
    label: "Heading 2",
    description: "Format as heading 2",
    icon: <TextHTwoIcon size={ICON_SIZE.sm} />,
  },
  heading3: {
    id: "heading3",
    label: "Heading 3",
    description: "Format as heading 3",
    icon: <TextHThreeIcon size={ICON_SIZE.sm} />,
  },
  bulletList: {
    id: "bulletList",
    label: "Bullet List",
    description: "Create bullet list",
    icon: <ListBulletsIcon size={ICON_SIZE.sm} />,
  },
  numberedList: {
    id: "numberedList",
    label: "Numbered List",
    description: "Create numbered list",
    icon: <ListNumbersIcon size={ICON_SIZE.sm} />,
  },
  checkList: {
    id: "checkList",
    label: "Check List",
    description: "Create check list",
    icon: <ListChecksIcon size={ICON_SIZE.sm} />,
  },
  quote: { id: "quote", label: "Quote", description: "Format as quote", icon: <QuotesIcon size={ICON_SIZE.sm} /> },
  link: { id: "link", label: "Insert Link", description: "Insert a link", icon: <LinkIcon size={ICON_SIZE.sm} /> },
  code: {
    id: "code",
    label: "Highlight Code",
    description: "Format as highlighted code",
    icon: <CodeSimpleIcon size={ICON_SIZE.sm} />,
  },
  table: { id: "table", label: "Table", description: "Insert a table", icon: <TableIcon size={ICON_SIZE.sm} /> },
  horizontalRule: {
    id: "horizontalRule",
    label: "Horizontal Rule",
    description: "Insert horizontal rule",
    icon: <MinusIcon size={ICON_SIZE.sm} />,
  },
  indent: {
    id: "indent",
    label: "Indent",
    description: "Indent content",
    icon: <TextIndentIcon size={ICON_SIZE.sm} />,
  },
  outdent: {
    id: "outdent",
    label: "Outdent",
    description: "Outdent content",
    icon: <TextOutdentIcon size={ICON_SIZE.sm} />,
  },
  codeBlock: {
    id: "codeBlock",
    label: "Code Block",
    description: "Format as code block",
    icon: <CodeIcon size={ICON_SIZE.sm} />,
  },
  equation: {
    id: "equation",
    label: "Equation",
    description: "Insert math equation (LaTeX)",
    icon: <FunctionIcon size={ICON_SIZE.sm} />,
  },
};

export const TOOLBAR_ITEMS = Object.values(TOOLBAR_ITEMS_MAP);

export const POPOVER_MIN_WIDTH_CLASS = "min-w-60";
export const AI_ACTION_MAX_WIDTH_CLASS = "sm:max-w-xs";
export const PAGE_LAYOUT_ID = "page-layout";
